import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Trash2 } from "lucide-react";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";
import PageHeader from "../../../components/PageHeader";
import Modal from "../../../components/Modal";
import {
  SELECT_STYLES,
} from "../../../components/DateFilters/constants";
import {
  controleProducaoService,
  SetorProducao,
} from "../../../services/controleProducaoService";
import { lojaService } from "../../../services/lojaService";
import { useAuth } from "../../../context/AuthContext";
import { canVerTodasLojasUser, hasFullAccessUser } from "../../../lib/screenAccess";
import { Loja } from "../../../types/loja";
import { COLORS } from "../../../constants";

type FormState = {
  id: number | null;
  nome: string;
  ativo: boolean;
};

const EMPTY_FORM: FormState = { id: null, nome: "", ativo: true };

type LojaOpt = { value: number; label: string };

const LOJA_SELECT_STYLES = SELECT_STYLES as unknown as StylesConfig<
  LojaOpt,
  false
>;

const ControleProducaoSetoresPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = hasFullAccessUser(user);
  const podeVerTodasLojas = canVerTodasLojasUser(user);
  const [lojas, setLojas] = useState<Loja[]>([]);
  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    user?.lojaId
  );
  const [setores, setSetores] = useState<SetorProducao[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const lojaOptions = useMemo(
    () =>
      podeVerTodasLojas
        ? lojas
        : lojas.filter((loja) => loja.id === user?.lojaId),
    [podeVerTodasLojas, lojas, user?.lojaId]
  );

  const lojaSelectOptions = useMemo<LojaOpt[]>(
    () => lojaOptions.map((loja) => ({ value: loja.id, label: loja.nome })),
    [lojaOptions]
  );

  const selectedLojaOption =
    lojaSelectOptions.find((o) => o.value === selectedLojaId) ?? null;

  const loadSetores = async (lojaId?: number) => {
    setLoading(true);
    try {
      const data = await controleProducaoService.listSetores(lojaId);
      setSetores(data);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Erro ao carregar setores.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadSetores(selectedLojaId);
  }, [selectedLojaId]);

  useEffect(() => {
    void lojaService
      .getLojas()
      .then(setLojas)
      .catch(() => setLojas([]));
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nome.trim()) {
      toast.warning("Informe o nome do setor.");
      return;
    }
    if (!selectedLojaId) {
      toast.warning("Selecione a loja.");
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        await controleProducaoService.updateSetor(form.id, {
          nome: form.nome,
          ativo: form.ativo,
          lojaId: selectedLojaId,
        });
        toast.success("Setor atualizado.");
      } else {
        await controleProducaoService.createSetor({
          nome: form.nome,
          lojaId: selectedLojaId,
        });
        toast.success("Setor criado.");
      }
      setModalOpen(false);
      setForm(EMPTY_FORM);
      await loadSetores(selectedLojaId);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Erro ao salvar setor.";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (setor: SetorProducao) => {
    const result = await Swal.fire({
      title: "Excluir setor?",
      text: `Remover "${setor.nome}"? Itens e usuários vinculados perderão este setor.`,
      icon: "warning",
      showCancelButton: true,
      cancelButtonText: "Cancelar",
      confirmButtonColor: COLORS.PRIMARY,
      confirmButtonText: "Sim, excluir",
    });
    if (!result.isConfirmed) return;
    try {
      await controleProducaoService.deleteSetor(setor.id);
      toast.success("Setor excluído.");
      await loadSetores(selectedLojaId);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Erro ao excluir setor.";
      toast.error(message);
    }
  };

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-4 mt-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <PageHeader
          title="Setores de Produção"
          description="Cadastre os setores da loja e vincule itens e operadores antes da primeira ficha DIA."
        />
        <div className="flex shrink-0 flex-wrap items-center gap-3">
          <ReactSelect<LojaOpt, false>
            inputId="setores-loja-select"
            value={selectedLojaOption}
            onChange={(option: SingleValue<LojaOpt>) =>
              setSelectedLojaId(option?.value)
            }
            options={lojaSelectOptions}
            isClearable={isAdmin}
            placeholder="Selecionar loja"
            className="w-full min-w-[220px] sm:w-60"
            styles={LOJA_SELECT_STYLES}
          />
          <button
            type="button"
            className="h-[38px] w-full rounded-md bg-primary px-4 text-sm font-semibold text-white sm:w-auto"
            onClick={() => {
              setForm(EMPTY_FORM);
              setModalOpen(true);
            }}
          >
            Novo Setor
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-neutral-600">
                Nome
              </th>
              <th className="px-4 py-3 text-left font-semibold text-neutral-600">
                Status
              </th>
              <th className="px-4 py-3 text-right font-semibold text-neutral-600">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {setores.map((setor) => (
              <tr key={setor.id} className="border-t border-neutral-200">
                <td className="px-4 py-3 font-medium text-neutral-800">
                  {setor.nome}
                </td>
                <td className="px-4 py-3">
                  {setor.ativo ? (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                      Ativo
                    </span>
                  ) : (
                    <span className="rounded-full bg-neutral-200 px-2 py-0.5 text-[11px] font-semibold text-neutral-600">
                      Inativo
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm"
                      onClick={() => {
                        setForm({
                          id: setor.id,
                          nome: setor.nome,
                          ativo: setor.ativo,
                        });
                        setModalOpen(true);
                      }}
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      title="Excluir setor"
                      className="rounded-md border border-red-200 p-1.5 text-red-600 hover:bg-red-50"
                      onClick={() => void handleDelete(setor)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && setores.length === 0 && (
              <tr>
                <td
                  className="px-4 py-6 text-center text-neutral-500"
                  colSpan={3}
                >
                  Nenhum setor cadastrado nesta loja.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {loading && (
        <p className="mt-3 text-sm text-neutral-500">Carregando setores...</p>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={form.id ? "Editar setor" : "Novo setor"}
      >
        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block text-sm font-medium text-neutral-700">
            Nome
            <input
              value={form.nome}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, nome: e.target.value }))
              }
              className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2"
            />
          </label>
          {form.id != null && (
            <label className="flex items-center gap-2 text-sm text-neutral-700">
              <input
                type="checkbox"
                checked={form.ativo}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, ativo: e.target.checked }))
                }
              />
              Setor ativo
            </label>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="rounded-md border border-neutral-300 px-3 py-2 text-sm"
              onClick={() => setModalOpen(false)}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {saving ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </form>
      </Modal>
    </main>
  );
};

export default ControleProducaoSetoresPage;
